import { BannerTypes, ISizeMapping } from '../../../models/banner/IGoogleTag';
import { PresentationBannerClusterInfo } from '../../../models/banner/cluster/PresentationBannerClusterInfo';
import { PresentationBannerInfo } from '../../../models/banner/PresentationBannerInfo';
import { TSearchResult } from '../../../models/search-result/TSearchResult';
import GoogleAdMethodsHandler from '../../../utils/GoogleAdMethodsHandler';

const advancedVisibilityBannerSizeMapping = (): ISizeMapping | undefined => {
	let sizeMapping = GoogleAdMethodsHandler.retrieveSizeMappingBuilder();
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [1096, 0], [[728, 90]]);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [752, 0], []);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [743, 0], [[728, 90]]);
	return sizeMapping;
};

const mobileAdvancedVisibilityBannerSizeMapping = (): ISizeMapping | undefined => {
	let sizeMapping = GoogleAdMethodsHandler.retrieveSizeMappingBuilder();
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [1096, 0], []);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [752, 0], [[320, 50]]);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [743, 0], []);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [352, 0], [[320, 50]]);
	return sizeMapping;
};

const leftTopVisibilityBannerSizeMapping = (): ISizeMapping | undefined => {
	let sizeMapping = GoogleAdMethodsHandler.retrieveSizeMappingBuilder();
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [332, 0], [[300, 250]]);
	return sizeMapping;
};

const rightTopVisibilityBannerSizeMapping = (): ISizeMapping | undefined => {
	let sizeMapping = GoogleAdMethodsHandler.retrieveSizeMappingBuilder();
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [984, 0], [[300, 250]]);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [752, 0], []);
	sizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(sizeMapping, [648, 0], [[300, 250]]);
	return sizeMapping;
};

const isBannerCluster = (item: TSearchResult): boolean => {
	return item instanceof PresentationBannerClusterInfo;
};

const isSingleBanner = (item: TSearchResult): boolean => {
	return item instanceof PresentationBannerInfo;
};

// Exported functions

const getBannerCode = (banner: PresentationBannerInfo | PresentationBannerClusterInfo, isMobile: boolean): string | null => {
	if (isBannerCluster(banner)) {
		return 'all__top_visibility/search__top_visibility';
	} else if (isSingleBanner(banner)) {
		return 'all_pages__advanced_visibility/search__advanced_visibility';
	} else if (isMobile) {
		return 'all_pages__mobile_advanced_visibility/search__mobile_advanced_visibility';
	} else return null;
};

const getBannerSizeMapping = (
	banner: PresentationBannerInfo | PresentationBannerClusterInfo,
	index: number,
	isMobile: boolean,
): ISizeMapping | undefined => {
	if (!window.googletag?.sizeMapping) {
		return;
	}

	let bannerSizeMapping: any;
	if (banner instanceof PresentationBannerInfo && !isMobile) {
		bannerSizeMapping = advancedVisibilityBannerSizeMapping();
	} else if (banner instanceof PresentationBannerInfo && isMobile) {
		bannerSizeMapping = mobileAdvancedVisibilityBannerSizeMapping();
	} else if (banner instanceof PresentationBannerClusterInfo) {
		if (index === 0) {
			bannerSizeMapping = leftTopVisibilityBannerSizeMapping();
		} else {
			bannerSizeMapping = rightTopVisibilityBannerSizeMapping();
		}
	}

	bannerSizeMapping = GoogleAdMethodsHandler.addSizeToSizeMapping(bannerSizeMapping, [0, 0], []);
	return GoogleAdMethodsHandler.buildSizeMapping(bannerSizeMapping);
};

const includeBannersInList = (searchResults: TSearchResult[]): TSearchResult[] => {
	if (searchResults.length > 10) {
		const advancedVisibilityBanner = new PresentationBannerInfo(
			'div-gpt-ad-22205299652-0',
			'BannerPlaceHolderAdvancedVisibility',
			BannerTypes.ADVANCED,
		);

		const mobileAdvancedVisibilityBanner = new PresentationBannerInfo(
			'div-gpt-ad-22356363616-0',
			'BannerPlaceHolderMobileAdvancedVisibility',
			BannerTypes.MOBILE_ADVANCED,
		);
		searchResults.splice(10, 0, advancedVisibilityBanner);
		searchResults.splice(10, 0, mobileAdvancedVisibilityBanner);
	}

	if (searchResults.length > 6) {
		const topVisibilityCluster = new PresentationBannerClusterInfo(
			['div-gpt-ad-22235797574-0', 'div-gpt-ad-22235797574-1'],
			'BannerPlaceHolderTopVisibility',
			BannerTypes.TOP,
		);
		searchResults.splice(6, 0, topVisibilityCluster);
	}

	return searchResults;
};

const getSlotTypeForAdUnit = (dimensions: number[]): string => {
	switch (dimensions[1]) {
		case 50:
			return 'mobileAdvanced';
		case 90:
			return 'advanced';
		case 100:
			return 'standard';
		case 250:
			return 'top';
		default:
			return '';
	}
};

const defineGoogleTagSlot = (
	code: string,
	dimensions: [number, number],
	containerId: string,
	sizeMapping: ISizeMapping | undefined,
): any => {
	if (!window.googletag || !window.adSlots) {
		return;
	}

	let slot = GoogleAdMethodsHandler.defineSlot(code, dimensions, containerId);
	GoogleAdMethodsHandler.addSizeMappingToSlot(slot, sizeMapping);
	slot = GoogleAdMethodsHandler.addServiceToDefinedSlot(slot);

	const slotType = getSlotTypeForAdUnit(dimensions);
	window.adSlots[slotType].push(slot);
};

// TODO: encapsulate all the logic to define size and slot (in a single file and export the function)
const defineAdSizeAndSlot = (
	banner: PresentationBannerInfo | PresentationBannerClusterInfo,
	defaultSize: [number, number],
	bannerId: string,
	index: number,
	isMobile = false,
): void => {
	const code = getBannerCode(banner, isMobile);
	const slotCode = `/21707809931/${code}/${bannerId}`;
	const bannerMapping = getBannerSizeMapping(banner, index, isMobile);

	defineGoogleTagSlot(slotCode, defaultSize, bannerId, bannerMapping);
};

export { defineAdSizeAndSlot, includeBannersInList, isBannerCluster, isSingleBanner };
